import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Download as DownloadIcon, ShieldCheck, Smartphone, Wifi, ArrowRight, CheckCircle2, AlertTriangle, Cable, Home, Clock4 } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { ContactSupportLink } from "@/components/app/ContactSupport";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

const API = process.env.REACT_APP_BACKEND_URL;

export default function Setup() {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/launcher/latest`, { withCredentials: true })
      .then((r) => setMeta(r.data))
      .catch((e) => setError(e?.message || "Failed to load"));
  }, []);

  const version = meta?.version || "1.2.0";
  const size = meta?.size_mb ?? 3.6;
  const downloadUrl = `${API}/api/launcher/download`;
  const changelog = meta?.changelog || [];
  const available = meta?.available !== false;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-5xl text-slate-900">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-4">Android TV launcher</div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold mb-5"
            style={headingFont}
            data-testid="setup-hero-heading"
          >
            Install Nexso on{" "}
            <span style={accentFont} className="text-sky-600">any Android TV</span>{" "}
            in 3 minutes.
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto mb-8" data-testid="setup-hero-sub">
            Sideload the Nexso launcher APK onto your Android stick, Smart TV or set-top box. No Google Play account needed. Works on Android 7.0 (Nougat) and newer.
          </p>

          {/* Primary CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
            <a
              href={available ? downloadUrl : "#"}
              className={`inline-flex items-center gap-2 rounded-full px-8 h-14 text-base font-semibold shadow-lg transition-all ${available ? "bg-sky-500 hover:bg-sky-600 text-white hover:shadow-xl" : "bg-slate-200 text-slate-500 cursor-not-allowed"}`}
              data-testid="setup-download-apk-button"
              download
              onClick={(e) => { if (!available) e.preventDefault(); }}
              aria-disabled={!available}
            >
              <DownloadIcon className="h-5 w-5" />
              {available ? `Download .APK · v${version}` : "APK coming soon"}
            </a>
            <a
              href="#instructions"
              className="inline-flex items-center gap-1.5 rounded-full px-6 h-14 text-base font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 border border-slate-200"
              data-testid="setup-instructions-link"
            >
              See install instructions
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <div className="text-xs text-slate-500 flex flex-wrap items-center justify-center gap-x-4 gap-y-1" data-testid="setup-meta">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Signed APK · safe to sideload</span>
            <span>·</span>
            <span>{size} MB</span>
            <span>·</span>
            <span>Android 7.0+</span>
            {error && <><span>·</span><span className="text-rose-500">Couldn’t reach update server</span></>}
          </div>
        </div>

        {/* Step-by-step */}
        <section id="instructions" className="mt-16">
          <h2 className="text-2xl sm:text-3xl tracking-tight font-semibold mb-8 text-center" style={headingFont} data-testid="setup-instructions-heading">
            Get your TV online in <span style={accentFont} className="text-sky-600">3 steps</span>.
          </h2>

          <div className="grid md:grid-cols-3 gap-5">
            <StepCard
              index={1}
              title="Connect to Wi-Fi"
              icon={<Wifi className="h-5 w-5 text-white" />}
              testId="setup-step-1"
            >
              Plug your Android stick into the TV&apos;s HDMI port. Power it on and complete the Android setup wizard — connecting to your home Wi-Fi.
            </StepCard>
            <StepCard
              index={2}
              title="Allow side-loading"
              icon={<Smartphone className="h-5 w-5 text-white" />}
              testId="setup-step-2"
            >
              Go to <strong>Settings → Security &amp; restrictions → Install unknown apps</strong>. Toggle ON for the browser or File Manager you&apos;ll use to install the APK.
            </StepCard>
            <StepCard
              index={3}
              title="Open this page on the TV"
              icon={<DownloadIcon className="h-5 w-5 text-white" />}
              testId="setup-step-3"
            >
              On the TV, open a browser and visit <span className="font-mono text-sky-700">nexso.net/download</span>. Tap the big sky-blue Download button above. When the file finishes, tap it to install.
            </StepCard>
          </div>
        </section>

        {/* After install */}
        <section className="mt-16">
          <div
            className="rounded-3xl p-8 md:p-10 border backdrop-blur-xl"
            style={{ background: "linear-gradient(135deg, rgba(14,165,233,0.08), rgba(56,189,248,0.05))", borderColor: "rgba(14,165,233,0.30)" }}
          >
            <div className="text-2xl sm:text-3xl tracking-tight font-semibold mb-4" style={headingFont} data-testid="setup-after-install-heading">
              After install — <span style={accentFont} className="text-sky-600">pair your TV</span>
            </div>
            <ol className="space-y-3 text-slate-700 text-sm sm:text-base">
              <li className="flex gap-3" data-testid="setup-after-step-1">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                <span>Open the <strong>Nexso</strong> app on your TV. It will display a 6-character pairing code &amp; QR.</span>
              </li>
              <li className="flex gap-3" data-testid="setup-after-step-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                <span>Go to <Link to="/dashboard/screens" className="text-sky-600 underline">Screens</Link> in your dashboard.</span>
              </li>
              <li className="flex gap-3" data-testid="setup-after-step-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                <span>Click <strong>Pair a TV</strong>, scan the QR or enter the 6-character code.</span>
              </li>
              <li className="flex gap-3" data-testid="setup-after-step-4">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                <span>Pick a template, customize it, and publish to your screen instantly.</span>
              </li>
            </ol>
            <div className="mt-6">
              <Link to="/dashboard/screens" data-testid="setup-go-screens">
                <Button className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-md h-11 px-6 font-semibold">
                  Go to Screens <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Pro tips */}
        <section className="mt-14 grid md:grid-cols-2 gap-5">
          <Card icon={<Home className="h-5 w-5 text-sky-600" />} title="Make Nexso your default Home app" testId="setup-tip-default-home">
            After the first install, Android may open your TV&apos;s stock launcher on boot. Open Nexso once — a <strong>“Set as Home”</strong> banner appears in the top-right. Tap <strong>Set as Home</strong> and pick Nexso. From then on, every power-on boots straight into your signage.
          </Card>
          <Card icon={<Clock4 className="h-5 w-5 text-emerald-500" />} title="Auto power on/off with a schedule" testId="setup-tip-schedule">
            Set business hours per screen (e.g. Mon-Fri 08:00-20:00) on the <Link to="/dashboard/screens" className="text-sky-600 underline">Screens</Link> page → click <strong>Hours</strong>. Nexso goes black outside the schedule and releases wake lock so the TV drops into standby. It wakes back up when the next open window starts.{" "}
            <Link to="/dashboard/screens" className="text-sky-600 underline">Set your schedule →</Link>
          </Card>
          <Card icon={<Cable className="h-5 w-5 text-sky-600" />} title="Use ADB for bulk-install" testId="setup-tip-adb">
            Deploying multiple sticks? Run our one-shot provisioning script — it installs the APK and sets Nexso as the default Home launcher in ~30 seconds per stick.{" "}
            <a className="text-sky-600 underline" href="https://github.com/emergent-nexso/nexso-launcher/blob/main/PROVISIONING.md" target="_blank" rel="noopener noreferrer">See the playbook →</a>
          </Card>
          <Card icon={<AlertTriangle className="h-5 w-5 text-amber-500" />} title="Stuck or locked out?" testId="setup-tip-recovery">
            If you can&apos;t get past the Wi-Fi screen or your TV won&apos;t accept the install, follow our recovery guide. Most issues come from older Android versions silently blocking sideloads — we have a fix.{" "}
            <ContactSupportLink category="tv" subject="Nexso Stick setup — stuck / locked out" testId="setup-contact-support-recovery">Contact support →</ContactSupportLink>
          </Card>
        </section>

        {/* Changelog */}
        {changelog.length > 0 && (
          <section className="mt-14" data-testid="setup-changelog">
            <h3 className="text-lg font-semibold tracking-tight mb-4" style={headingFont}>What&apos;s new in v{version}</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              {changelog.map((c, i) => (
                <li key={i} className="flex gap-2" data-testid={`setup-changelog-item-${i}`}>
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </DashboardLayout>
  );
}

function StepCard({ index, title, icon, children, testId }) {
  return (
    <div
      className="rounded-2xl p-6 border backdrop-blur-xl bg-white/80"
      style={{ borderColor: "#E2E8F0" }}
      data-testid={testId}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center shadow-sm">
          {icon}
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-sky-600 font-medium">Step {index}</div>
      </div>
      <div className="text-lg font-semibold tracking-tight mb-2" style={headingFont}>{title}</div>
      <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}

function Card({ icon, title, children, testId }) {
  return (
    <div className="rounded-2xl p-6 border bg-white" style={{ borderColor: "#E2E8F0" }} data-testid={testId}>
      <div className="flex items-center gap-2.5 mb-2">
        {icon}
        <div className="text-base font-semibold tracking-tight" style={headingFont}>{title}</div>
      </div>
      <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}
